/**
 * Future Home Plan Foundation (FHPF) - Client Circuit Breaker & Automatic Failover Engine
 * 
 * Provides automated resilience and high availability:
 * 1. Monitors cPanel backend health.
 * 2. Trips circuit on consecutive failures, switching to direct Firestore read-only mode.
 * 3. Probes health in HALF_OPEN state and gracefully auto-recovers.
 */

export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export interface CircuitBreakerConfig {
  endpoints?: string[];
  primaryUrl?: string;
  fallbackUrl?: string;
  failureThreshold: number; // consecutive failures before opening circuit
  resetTimeoutMs: number; // cooldown before probing (HALF_OPEN)
}

export class CircuitBreaker {
  private state: CircuitState = 'CLOSED';
  private failureCount: number = 0;
  private lastFailureTime: number = 0;
  private currentEndpointIndex: number = 0;
  private endpoints: string[];
  private config: CircuitBreakerConfig;
  private listeners: Array<(state: CircuitState, activeUrl: string) => void> = [];

  constructor(config?: Partial<CircuitBreakerConfig>) {
    const list: string[] = [];
    if (config?.endpoints && config.endpoints.length > 0) {
      list.push(...config.endpoints);
    } else {
      if (config?.primaryUrl) list.push(config.primaryUrl);
      if (import.meta.env.VITE_API_BASE_URL) list.push(import.meta.env.VITE_API_BASE_URL);
      list.push('https://fhpf.viraltui.com');
      list.push('https://fhpf.viraltui.com/api');
      list.push('https://fhpf.viraltui.bond/api');
      list.push('https://fhpf.ashikur.bond/api');
      list.push('https://fhpf.viraltui.cyou/api');
      list.push('https://fhpf.ashikur.cyou/api');
      list.push('https://viraltui.com/api');
    }
    // Deduplicate while maintaining order
    this.endpoints = Array.from(new Set(list));

    this.config = {
      endpoints: this.endpoints,
      failureThreshold: config?.failureThreshold || 2,
      resetTimeoutMs: config?.resetTimeoutMs || 30000, // 30s
    };
  }

  public getState(): CircuitState {
    // Check if cooldown has elapsed while OPEN
    if (this.state === 'OPEN') {
      const now = Date.now();
      if (now - this.lastFailureTime > this.config.resetTimeoutMs) {
        this.setState('HALF_OPEN');
      }
    }
    return this.state;
  }

  public getActiveUrl(): string {
    return this.endpoints[this.currentEndpointIndex % this.endpoints.length];
  }

  public isReadOnlyMode(): boolean {
    return this.getState() === 'OPEN';
  }

  public recordSuccess(): void {
    if (this.state !== 'CLOSED') {
      console.log('[CircuitBreaker] Service healthy on:', this.getActiveUrl());
    }
    this.failureCount = 0;
    this.setState('CLOSED');
  }

  public recordFailure(err?: any): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    console.warn(
      `[CircuitBreaker] Endpoint failure (${this.failureCount}) on ${this.getActiveUrl()}:`,
      err?.message || err
    );

    // Failover to next endpoint in the list
    if (this.endpoints.length > 1) {
      this.currentEndpointIndex = (this.currentEndpointIndex + 1) % this.endpoints.length;
      console.info(`[CircuitBreaker] Auto-failing over to next endpoint: ${this.getActiveUrl()}`);
    }

    if (this.failureCount >= this.config.failureThreshold * this.endpoints.length) {
      this.setState('OPEN');
      console.error(
        '[CircuitBreaker] ⚠️ All backend endpoints failed. Operating in read-only mode.'
      );
    }
  }

  public onStateChange(listener: (state: CircuitState, activeUrl: string) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private setState(newState: CircuitState): void {
    if (this.state !== newState) {
      this.state = newState;
      const activeUrl = this.getActiveUrl();
      this.listeners.forEach((listener) => listener(newState, activeUrl));
    }
  }
}

// Global Singleton Circuit Breaker instance
export const globalCircuitBreaker = new CircuitBreaker();
